
import { supabase } from '@/integrations/supabase/client';
import { ChatMessage } from '@/types/chat';

export const supabaseChatMessagesService = {
  // Get messages for a chat group
  async getChatMessages(chatGroupId: string, limit?: number): Promise<ChatMessage[]> {
    console.log('=== GETTING CHAT MESSAGES ===');
    console.log('Chat Group ID:', chatGroupId, 'Limit:', limit);
    
    let query = supabase
      .from('chat_messages')
      .select('*')
      .eq('chat_group_id', chatGroupId)
      .order('created_at', { ascending: true });

    if (limit) {
      query = query.limit(limit);
    }

    const { data: messages, error } = await query;

    if (error) {
      console.error('❌ Error fetching chat messages:', error);
      return [];
    }

    if (!messages || messages.length === 0) {
      console.log('📭 No messages found for group:', chatGroupId);
      return [];
    }

    console.log(`📬 Found ${messages.length} messages for group:`, chatGroupId);

    // Get user profiles
    const userIds = [...new Set(messages.map(msg => msg.user_id).filter(Boolean))];
    console.log('👥 User IDs from messages:', userIds);
    
    const [supabaseProfiles, internalUsers] = await Promise.all([
      supabase
        .from('profiles')
        .select('id, username, first_name, last_name')
        .in('id', userIds),
      supabase
        .from('internal_users')
        .select('id, username')
        .in('id', userIds)
    ]);

    // Enhance messages with user profiles
    const messagesWithProfiles = messages.map(msg => {
      const supabaseProfile = supabaseProfiles.data?.find(p => p.id === msg.user_id);
      const internalProfile = internalUsers.data?.find(u => u.id === msg.user_id);
      
      let userProfile;
      if (supabaseProfile) {
        userProfile = {
          username: supabaseProfile.username || supabaseProfile.first_name || 'Supabase User',
          first_name: supabaseProfile.first_name || '',
          last_name: supabaseProfile.last_name || ''
        };
      } else if (internalProfile) {
        userProfile = {
          username: internalProfile.username || 'New School User',
          first_name: internalProfile.username || '',
          last_name: ''
        };
      } else {
        userProfile = {
          username: `User-${msg.user_id?.slice(0, 8) || 'Unknown'}`,
          first_name: '',
          last_name: ''
        };
      }
      
      return {
        ...msg,
        user_profile: userProfile
      };
    });

    return messagesWithProfiles as ChatMessage[];
  },

  // Send a message
  async sendMessage(chatGroupId: string, message: string, currentUser?: any): Promise<ChatMessage> {
    console.log('=== SENDING MESSAGE WITH FIXED NOTIFICATIONS ===');
    console.log('Chat Group ID:', chatGroupId);
    console.log('Message:', message);
    console.log('Current User:', currentUser?.username, 'Type:', currentUser?.userType);
    
    let userId: string;
    let userProfile: any = null;
    
    // Get current user
    const { data: { user: supabaseUser } } = await supabase.auth.getUser();
    
    if (supabaseUser) {
      userId = supabaseUser.id;
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('username, first_name, last_name')
        .eq('id', userId)
        .single();
      
      userProfile = {
        username: profile?.username || profile?.first_name || 'Supabase User',
        first_name: profile?.first_name || '',
        last_name: profile?.last_name || ''
      };
    } else if (currentUser) {
      userId = currentUser.id;
      userProfile = {
        username: currentUser.username || 'New School User',
        first_name: currentUser.username || '',
        last_name: ''
      };
    } else {
      throw new Error('User not authenticated');
    }

    // Get group info
    const { data: groupData } = await supabase
      .from('chat_groups')
      .select('chat_name')
      .eq('chat_id', chatGroupId)
      .single();

    // Insert the message
    const { data, error } = await supabase
      .from('chat_messages')
      .insert({
        chat_group_id: chatGroupId,
        user_id: userId,
        message: message
      })
      .select()
      .single();

    if (error) {
      console.error('❌ Error sending message:', error);
      throw error;
    }

    const newMessage = {
      ...data,
      user_profile: userProfile
    };

    // FIXED: Create notifications for mentioned users
    await this.createNotificationsForMentions(newMessage, chatGroupId, groupData?.chat_name || 'Unknown Group');

    return newMessage as ChatMessage;
  },

  // Mark messages as read
  async markMessagesAsRead(chatGroupId: string): Promise<void> {
    console.log('=== MARKING MESSAGES AS READ ===');
    console.log('Chat Group ID:', chatGroupId);
    
    try {
      // Get current user
      const { data: { user: supabaseUser } } = await supabase.auth.getUser();
      
      if (!supabaseUser) {
        console.log('No user authenticated, skipping mark as read');
        return;
      }

      // Update messages to mark as read
      const { error } = await supabase
        .from('chat_messages')
        .update({ is_read: true })
        .eq('chat_group_id', chatGroupId)
        .neq('user_id', supabaseUser.id); // Don't mark own messages

      if (error) {
        console.error('❌ Error marking messages as read:', error);
      } else {
        console.log('✅ Messages marked as read for group:', chatGroupId);
      }
    } catch (error) {
      console.error('❌ Error in markMessagesAsRead:', error);
    }
  },

  // FIXED: Create notifications for mentioned users with enhanced debugging
  async createNotificationsForMentions(message: ChatMessage, chatGroupId: string, chatGroupName: string): Promise<void> {
    console.log('=== CREATING NOTIFICATIONS FOR MENTIONS - FIXED VERSION ===');
    console.log('Message sender:', message.user_id);
    console.log('Group:', chatGroupName);
    console.log('Message content:', message.message);
    
    try {
      // ENHANCED: Parse mentions from message
      const mentions = this.parseMentions(message.message);
      console.log('🏷️ FIXED - Parsed mentions from message:', mentions);

      if (mentions.length === 0) {
        console.log('📭 FIXED - No mentions found - no notifications to create');
        return;
      }

      // Get ALL active users in the chat group
      const { data: chatGroupUsers } = await supabase
        .from('chat_group_users')
        .select('user_id')
        .eq('chat_group_id', chatGroupId)
        .eq('is_active', true)
        .neq('user_id', message.user_id); // Exclude sender

      if (!chatGroupUsers || chatGroupUsers.length === 0) {
        console.log('📭 FIXED - No other members in group to notify');
        return;
      }

      console.log(`👥 FIXED - Found ${chatGroupUsers.length} potential users to check for mentions`);

      // Get BOTH Supabase and Internal users for comprehensive matching
      const userIds = chatGroupUsers.map(user => user.user_id);
      
      const [supabaseProfiles, internalUsers] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, username, first_name')
          .in('id', userIds),
        supabase
          .from('internal_users')
          .select('id, username')
          .in('id', userIds)
      ]);

      console.log('👤 FIXED - Supabase profiles found:', supabaseProfiles.data?.length || 0);
      console.log('👤 FIXED - Internal users found:', internalUsers.data?.length || 0);
      console.log('👤 FIXED - All Supabase profiles:', supabaseProfiles.data?.map(p => ({ id: p.id, username: p.username, first_name: p.first_name })));
      console.log('👤 FIXED - All Internal users:', internalUsers.data?.map(u => ({ id: u.id, username: u.username })));

      // No longer using localStorage - all notifications go to database

      // FIXED: Check each group member against mentions with enhanced logging
      for (const groupUser of chatGroupUsers) {
        const supabaseProfile = supabaseProfiles.data?.find(p => p.id === groupUser.user_id);
        const internalProfile = internalUsers.data?.find(u => u.id === groupUser.user_id);
        
        const username = supabaseProfile?.username || 
                        supabaseProfile?.first_name || 
                        internalProfile?.username || 
                        'Unknown';

        console.log(`🔍 FIXED - Checking user ${username} (${groupUser.user_id}) against mentions:`, mentions);

        // FIXED: Enhanced mention matching with detailed logging
        const isDirectMention = mentions.some(mention => {
          const mentionClean = mention.toLowerCase().trim();
          const usernameClean = username.toLowerCase().trim();
          const firstNameClean = (supabaseProfile?.first_name || '').toLowerCase().trim();
          
          const matchesUsername = mentionClean === usernameClean;
          const matchesFirstName = mentionClean === firstNameClean;
          
          console.log(`  🔍 FIXED - Mention "${mention}" vs User "${username}":`, {
            mentionClean,
            usernameClean,
            firstNameClean,
            matchesUsername,
            matchesFirstName
          });
          
          return matchesUsername || (firstNameClean && matchesFirstName);
        });

        if (isDirectMention) {
          console.log(`✅ CREATING DATABASE NOTIFICATION for user: ${username} (${groupUser.user_id})`);
          
          // Use the unified notification service to create notifications in the database
          const { unifiedNotificationService } = await import('@/services/unifiedNotificationService');
          
          await unifiedNotificationService.createNotification({
            type: 'mention',
            chat_group_id: chatGroupId,
            message_id: message.message_id,
            from_user_id: message.user_id,
            to_user_id: groupUser.user_id,
            message_preview: message.message.length > 100 
              ? message.message.substring(0, 100) + '...' 
              : message.message,
            chat_group_name: chatGroupName,
            from_username: message.user_profile?.username || 'Unknown User',
            is_read: false
          });
          
          console.log(`✅ DATABASE NOTIFICATION CREATED for: ${username}`);
        } else {
          console.log(`⏭️ FIXED - User ${username} not mentioned - no notification created`);
        }
      }
      
      console.log('✅ All mention notifications processed and saved to database');
      
      // Multiple event types for better compatibility
      const eventTypes = [
        'new-notification',
        'unified-notification',
        'notification-update',
        'notification-bell-update',
        'enhanced-notification-trigger'
      ];
      
      eventTypes.forEach(eventType => {
        window.dispatchEvent(new CustomEvent(eventType, { 
          detail: { 
            type: 'new',
            message: message,
            groupName: chatGroupName,
            timestamp: new Date().toISOString()
          } 
        }));
        console.log(`📡 FIXED - Broadcast ${eventType} event sent`);
      });
      
    } catch (error) {
      console.error('❌ FIXED - Error in createNotificationsForMentions:', error);
    }
  },

  // FIXED: Enhanced mention parsing with better regex
  parseMentions(message: string): string[] {
    console.log('🔍 FIXED - Parsing mentions from message:', message);
    
    // Enhanced regex to catch @username patterns (alphanumeric, underscores, hyphens)
    const mentionRegex = /@([a-zA-Z0-9_-]+)/g;
    const mentions: string[] = [];
    let match;
    
    while ((match = mentionRegex.exec(message)) !== null) {
      mentions.push(match[1]); // Get the username without @
    }
    
    const uniqueMentions = [...new Set(mentions)];
    console.log('📝 FIXED - Extracted mentions:', uniqueMentions);
    return uniqueMentions;
  }
};
